



import React, { useState, useEffect } from 'react';
import {Box,Card,CardContent,Typography,Button,Stack,Divider,IconButton,Alert,CircularProgress,TextField,Select,MenuItem,FormControl,InputLabel,Radio,RadioGroup,FormControlLabel,Snackbar,Chip
} from '@mui/material';
import {ArrowBack,ReportProblem,Send,CameraAlt,Close,LocationOn,Phone
} from '@mui/icons-material';
import axios from 'axios';
// Changed to accept props: order, onBack, onIssueSubmitted
const IssueReporting = ({ order: prefilledOrder, onBack, onIssueSubmitted }) => {



  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(prefilledOrder?.id || '');
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  console.log('IssueReporting - Prefilled order:', prefilledOrder); // Debug
  console.log('IssueReporting - Selected order ID:', selectedOrder); // Debug

  // Issue types
  const issueTypes = [
    { value: 'customer_unavailable', label: 'Customer Unavailable', description: 'Customer not reachable or not at home' },
    { value: 'wrong_address', label: 'Wrong Address', description: 'Address is incorrect or incomplete' },
    { value: 'payment_issue', label: 'Payment Issue', description: 'Customer unable or refusing to pay' },
    { value: 'product_mismatch', label: 'Product Mismatch', description: 'Order items don\'t match customer expectation' },
    { value: 'damaged_package', label: 'Damaged Package', description: 'Package damaged during transit' },
    { value: 'customer_refused', label: 'Customer Refused Delivery', description: 'Customer rejected the delivery' },
    { value: 'safety_concern', label: 'Safety Concern', description: 'Unsafe delivery location or situation' },
    { value: 'vehicle_breakdown', label: 'Vehicle Breakdown', description: 'Vehicle issues preventing delivery' },
    { value: 'other', label: 'Other Issue', description: 'Any other issue not listed above' }
  ];

  useEffect(() => {
    fetchActiveOrders();
  }, []);

  useEffect(() => {
    // If we have a prefilled order and it exists in the fetched orders, keep it selected
    if (prefilledOrder && orders.length > 0) {
      const orderExists = orders.find(o => o.id === prefilledOrder.id);
      console.log('useEffect - Order exists in list:', orderExists); // Debug
      if (orderExists) {
        setSelectedOrder(prefilledOrder.id);
        console.log('useEffect - Setting selected order to:', prefilledOrder.id); // Debug
      }
    }
  }, [prefilledOrder, orders]);

  const fetchActiveOrders = async () => {
    try {
      const userId = sessionStorage.getItem('userId');
      
      const response = await axios.get(
        `${process.env.REACT_APP_API}/deliveryagent/my-orders`,
        {
          params: { 
            user_id: userId,
            filter: 'active' 
          },
          headers: { 'X-User-Id': userId }
        }
      );

      if (response.data.success) {
        console.log('Orders fetched:', response.data.data);
        setOrders(response.data.data || []);              
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedPhoto(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitIssue = async () => {
    try {
      // Validation
      if (!selectedOrder) {
        setSnackbar({
          open: true,
          message: 'Please select an order',
          severity: 'warning'
        });
        return;
      }

      if (!issueType) {
        setSnackbar({
          open: true,
          message: 'Please select an issue type',
          severity: 'warning'
        });
        return;
      }

      if (!description.trim()) {
        setSnackbar({
          open: true,
          message: 'Please describe the issue',
          severity: 'warning'
        });
        return;
      }

      setLoading(true);
      const userId = sessionStorage.getItem('userId');
      
      const order = orders.find(o => o.id === selectedOrder);

      // Prepare form data for photo upload
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('orderId', order.id);
      formData.append('orderType', order.orderType);
      formData.append('issueType', issueType);
      formData.append('description', description);
      
      if (selectedPhoto) {
        formData.append('photo', selectedPhoto);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API}/deliveryagent/report-issue`,
        formData,
        {
          headers: {
            'X-User-Id': userId,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Issue reported successfully! Support team will contact you.',
          severity: 'success'
        });

        // Reset form
        setTimeout(() => {
          setSelectedOrder('');
          setIssueType('');
          setDescription('');
          setSelectedPhoto(null);
          setPhotoPreview(null);
          if (onIssueSubmitted && typeof onIssueSubmitted === 'function') {
            onIssueSubmitted(); 
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Issue reporting error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to report issue',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedOrderDetails = orders.find(o => o.id === selectedOrder);

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>

        <IconButton onClick={onBack}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight={600} color="#dc3545">
          <ReportProblem sx={{ mr: 1, verticalAlign: 'middle' }} />
          Report Delivery Issue
        </Typography>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Report any issues you encounter during delivery. Our support team will review and assist you.
      </Alert>

      {/* Order Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Select Order
          </Typography>
          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth>
            <InputLabel>Choose Order</InputLabel>
            <Select
              value={selectedOrder}
              onChange={(e) => setSelectedOrder(e.target.value)}
              label="Choose Order"
            >
              <MenuItem value="">
                <em>Select an order...</em>
              </MenuItem>
              {orders.map((order) => (
                <MenuItem key={order.id} value={order.id}>
                  <Stack direction="row" spacing={2} alignItems="center" width="100%">
                    <Typography>
                      #{order.orderId} - {order.customerName}
                    </Typography>
                    <Chip 
                      label={order.deliveryStatus || order.orderStatus}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Selected Order Details */}
          {selectedOrderDetails && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Order Details:
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Customer:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedOrderDetails.customerName}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Phone:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    <a href={`tel:${selectedOrderDetails.customerPhone}`} style={{ color: '#006D77', textDecoration: 'none' }}>
                      <Phone fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      {selectedOrderDetails.customerPhone}
                    </a>
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Amount:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    ₹{selectedOrderDetails.totalAmount}
                  </Typography>
                </Box>
                {selectedOrderDetails.customerAddress && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      Address:
                    </Typography>
                    <Typography variant="body2">
                      {selectedOrderDetails.customerAddress.fullAddress}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Issue Type Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Issue Type
          </Typography>
          <Divider sx={{ my: 2 }} />

          <RadioGroup value={issueType} onChange={(e) => setIssueType(e.target.value)}>
            {issueTypes.map((type) => (
              <Box
                key={type.value}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  p: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  ...(issueType === type.value && {
                    bgcolor: '#e7f3ff',
                    borderColor: '#006D77'
                  })
                }}
                onClick={() => setIssueType(type.value)}
              >
                <FormControlLabel
                  value={type.value}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {type.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Box>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Issue Description */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Describe the Issue
          </Typography>
          <Divider sx={{ my: 2 }} />

          <TextField
            fullWidth
            multiline
            rows={5}
            label="Detailed Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please provide detailed information about the issue. Include what happened, when it occurred, and any other relevant details..."
            required
            helperText="Be as specific as possible to help us resolve the issue quickly"
          />
        </CardContent>
      </Card>

      {/* Photo Upload */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Attach Photo (Optional)
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} alignItems="center">
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="issue-photo-upload"
              type="file"
              capture="environment"
              onChange={handlePhotoChange}
            />
            <label htmlFor="issue-photo-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CameraAlt />}
              >
                Take Photo
              </Button>
            </label>
          </Stack>

          {photoPreview && (
            <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
              <img
                src={photoPreview}
                alt="Issue evidence"
                style={{
                  maxWidth: '300px',
                  maxHeight: '300px',
                  borderRadius: '8px',
                  border: '2px solid #ddd'
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  top: 5,
                  right: 5,
                  bgcolor: 'rgba(255,255,255,0.9)'
                }}
                onClick={() => {
                  setSelectedPhoto(null);
                  setPhotoPreview(null);
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Important Notes */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          Important Notes:
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Ensure you've attempted to contact the customer before reporting</li>
            <li>Take photos of any damaged items or issues as evidence</li>
            <li>Our support team will review and respond within 1-2 hours</li>
            <li>For urgent issues, call support:  +91 7661889977</li>
          </ul>
        </Typography>
      </Alert>

      {/* Submit Button */}
      <Stack spacing={2}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleSubmitIssue}
          disabled={loading || !selectedOrder || !issueType || !description.trim()}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
          sx={{
            bgcolor: '#dc3545',
            '&:hover': { bgcolor: '#c82333' },
            '&:disabled': { bgcolor: '#ccc' }
          }}
        >
          {loading ? 'Submitting Issue...' : 'Submit Issue Report'}
        </Button>

        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={onBack} 
          disabled={loading}
        >
          Cancel
        </Button>
      </Stack>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IssueReporting;